import { ContentCopy } from '@mui/icons-material';
import { CircularProgress, Stack, Typography } from '@mui/material';
import { Controller, useForm } from 'react-hook-form';

import { importTourEventsFromOrfeo } from '@/domain/project-tour-ontour/actions/import-tour-events-from-orfeo';
import { TourEventBasic } from '@/domain/project-tour-ontour/entities/project-tour-ontour-event.entity';
import { selectProjecTourOnTourIsImportingTourEvents } from '@/domain/project-tour-ontour/project-tour-ontour.selectors';
import { CloseButton } from '@/ui/components/button/CloseButton';
import { FSPrimaryButton } from '@/ui/components/button/FSPrimaryButton';
import { FSCard } from '@/ui/components/FSCard';
import { FSLink } from '@/ui/components/FSLink';
import { FSDateField } from '@/ui/components/input/FSDateField';
import { FSTextField } from '@/ui/components/input/FSTextField';
import { FSModal } from '@/ui/components/modal/FSModal';
import { FSModalTitle } from '@/ui/components/modal/FSModalTitle';
import { useAppDispatch, useAppSelector } from '@/ui/hook/store';
import { translate } from '@/ui/i18n';
import { ColorsRaw } from '@/ui/theme/colors-raw';

export function ModalImportApiOrfeoTour({
  open,
  onClose,
  onImportTourEventsBasic,
}: {
  open: boolean;
  onClose: () => void;
  onImportTourEventsBasic: (tourEventsBasic: TourEventBasic[]) => void;
}) {
  return (
    <FSModal open={open} onClose={onClose} maxWidth={600}>
      <Stack spacing={3}>
        <Stack direction="row" justifyContent="space-between">
          <FSModalTitle
            title={translate(
              'ProjectsTour.OnTour.ImportApiOrfeoModalTour.title',
            )}
          />
          <CloseButton onClick={onClose} />
        </Stack>
        <HowToUseMessage />
        <MaxResultsMessage />
        <ImportForm
          onImportTourEventsBasic={(tourEventsBasic) => {
            onClose();
            onImportTourEventsBasic(tourEventsBasic);
          }}
        />
      </Stack>
    </FSModal>
  );
}

function HowToUseMessage() {
  const ORFEO_APP_URL = 'https://orfeoapp.com';
  const ORFEO_APP_SETTING_API_URL =
    'https://orfeoapp.com/accounts/my-organization/api';
  const ORFEO_COPY_ICON_COLOR = '2A6FFD';
  return (
    <FSCard
      variant="elevation"
      sx={{ bgcolor: ColorsRaw.anthracite.Warm_Anthracite_2 }}
    >
      <Stack sx={{ p: 2, gap: 2 }}>
        <Typography>
          {'Un token API orfeo est nécéssaire pour cette action.'}
        </Typography>
        <Typography>
          {'Connectez vous à votre compte orfeo :'}
          <br />
          <FSLink href={ORFEO_APP_URL} target="_blank">
            {ORFEO_APP_URL}
          </FSLink>
        </Typography>
        <Typography>
          {'Accédez à la page "API & webhooks" :'}
          <br />
          <FSLink href={ORFEO_APP_SETTING_API_URL} target="_blank">
            {ORFEO_APP_SETTING_API_URL}
          </FSLink>
        </Typography>
        <Typography>
          {"Générez une nouvelle clé d'API :"} <br />
          {'- nom : fairly (par exemple)'}
          <br />
          {
            '- permission : cochez au moins "Accès aux données liées aux productions"'
          }
          <br />
          {'> cliquer sur Ajouter'}
          <br />
        </Typography>
        <Typography>
          {"Copiez la clé d'API générée."} {'(bouton '}
          <ContentCopy sx={{ fontSize: 15, color: ORFEO_COPY_ICON_COLOR }} />
          {' )'}
        </Typography>
        <Typography>
          {'Collez cette clé dans le champ Token API Orfeo ci dessous.'}
        </Typography>
      </Stack>
    </FSCard>
  );
}

function MaxResultsMessage() {
  return (
    <FSCard
      variant="elevation"
      sx={{ bgcolor: ColorsRaw.anthracite.Warm_Anthracite_2 }}
    >
      <Stack sx={{ p: 2, gap: 2 }}>
        <Typography>
          {"Cette action importe jusqu'à 60 dates maximun."}
        </Typography>
        <Typography>
          {
            "Pour en importer d'avantage, répétez l'opération en ajustant les dates de début et de fin."
          }
        </Typography>
      </Stack>
    </FSCard>
  );
}

type Inputs = {
  apiToken: string;
  artistId: string; // named "ensemble" in orfeo data model
  spectacleId: string;
  startDate: string;
  endDate: string;
};

function ImportForm({
  onImportTourEventsBasic,
}: {
  onImportTourEventsBasic: (tourEventsBasic: TourEventBasic[]) => void;
}) {
  const dispatch = useAppDispatch();

  const isImporting = useAppSelector(
    selectProjecTourOnTourIsImportingTourEvents,
  );

  const { handleSubmit, register, formState, control } = useForm<Inputs>({
    defaultValues: {
      apiToken: '',
      artistId: '',
      spectacleId: '',
      startDate: '',
      endDate: '',
    },
  });

  const { errors } = formState;

  const onSubmit = async (data: Inputs) => {
    const tourEventsBasic: TourEventBasic[] = await dispatch(
      importTourEventsFromOrfeo({
        apiToken: data.apiToken,
        spectacleId: data.spectacleId,
        artistId: data.artistId,
        startDate: data.startDate || '',
        endDate: data.endDate || '',
      }),
    ).unwrap();
    onImportTourEventsBasic(tourEventsBasic);
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <Stack>
        <FSTextField
          label={translate(
            'ProjectsTour.OnTour.ImportApiOrfeoModalTour.form.api-token',
          )}
          type="text"
          register={{
            ...register('apiToken', {
              required: translate('Common.field-is-required'),
            }),
          }}
          error={!!errors.apiToken}
          helperText={errors.apiToken?.message}
        />
        <FSTextField
          label={translate(
            'ProjectsTour.OnTour.ImportApiOrfeoModalTour.form.spectacleId',
          )}
          type="text"
          register={{ ...register('spectacleId') }}
          error={!!errors.spectacleId}
          helperText={errors.spectacleId?.message}
        />
        <FSTextField
          label={translate(
            'ProjectsTour.OnTour.ImportApiOrfeoModalTour.form.artistId',
          )}
          type="text"
          register={{ ...register('artistId') }}
          error={!!errors.artistId}
          helperText={errors.artistId?.message}
        />
      </Stack>
      <Stack sx={{ display: 'flex', flexDirection: 'row', gap: 2 }}>
        <Controller
          name={'startDate'}
          control={control}
          render={({ field }) => (
            <FSDateField
              label={translate(
                'ProjectsTour.OnTour.ImportApiOrfeoModalTour.form.start-date',
              )}
              value={field.value}
              onChange={field.onChange}
              name={field.name}
              error={!!errors.startDate}
              helperText={errors.startDate?.message}
            />
          )}
        />
        <Controller
          name={'endDate'}
          control={control}
          render={({ field }) => (
            <FSDateField
              label={translate(
                'ProjectsTour.OnTour.ImportApiOrfeoModalTour.form.end-date',
              )}
              value={field.value}
              onChange={field.onChange}
              name={field.name}
              error={!!errors.endDate}
              helperText={errors.endDate?.message}
            />
          )}
        />
      </Stack>
      <Stack
        sx={{
          flexDirection: 'row',
          alignItems: 'center',
          justifyContent: 'right',
          gap: 2,
          mt: 2,
        }}
      >
        {isImporting && <CircularProgress size={20} />}
        <FSPrimaryButton type="submit" disabled={isImporting}>
          {translate('ProjectsTour.OnTour.ImportApiOrfeoModalTour.form.submit')}
        </FSPrimaryButton>
      </Stack>
    </form>
  );
}
